import React from 'react';
import { List, ListItemButton, ListItemIcon, ListItemText, Collapse, Box, Divider, ListSubheader } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  EmojiEvents as EmojiEventsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Tune as TuneIcon,
  EscalatorWarning as StudentIcon,
  Business as SchoolIcon,
  AdminPanelSettings as SuperIcon,
  MilitaryTech as AwardsIcon,
  VerifiedUser as WwccIcon,
  Description as FileIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth';
import packageJson from '../../package.json';
import VersionDialog from './dialogs/VersionDialog';

const drawerWidth = 250;

function NavMenu() {
  const { user } = useAuth();
  const location = useLocation();
  const [openSettings, setOpenSettings] = React.useState(
    location.pathname.startsWith('/settings')
  );
  const [showLog, setShowLog] = React.useState(false);

  // Helper to determine if we are in "Global View"
  // If user is superuser and no tenant is selected, this is true.
  const isGlobalView = user?.role === 'superuser' && !user?.tenant_id;

  const isModuleEnabled = (moduleName) => {
    // 1. If we are in Global View (no tenant selected), hide all modules
    if (isGlobalView) return false;

    // 2. If a tenant is selected:
    // We check if the module is actually enabled for THIS specific tenant.
    // This applies to both admins AND superusers.
    return user?.enabled_modules?.includes(moduleName);
  };


  return (
    <>

      <Box sx={{
        width: drawerWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.100',
        borderRight: 1,
        borderColor: 'divider'
      }}>

        <List
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            '& .MuiListItemButton-root': {
              py: 0.5, // Shaves off vertical padding
            },
            '& .MuiListItemIcon-root': {
              minWidth: 40, // Brings the icon closer to the text
            }
          }}
        >
          {!isGlobalView && (
            <>
              {/* DASHBOARD */}
              <ListItemButton component={Link} to="/" selected={location.pathname === '/'}>
                <ListItemIcon><DashboardIcon /></ListItemIcon>
                <ListItemText primary="Dashboard" />
              </ListItemButton>

              {/* AWARDS */}
              {isModuleEnabled('awards') && (
                <ListItemButton component={Link} to="/ceremonies" selected={location.pathname === '/ceremonies'}>
                  <ListItemIcon><EmojiEventsIcon /></ListItemIcon>
                  <ListItemText primary="Achievement Ceremonies" />
                </ListItemButton>
              )}

              {/* WWCC */}
              {isModuleEnabled('wwcc') && (
                <ListItemButton component={Link} to="/wwcc" selected={location.pathname === '/wwcc'}>
                  <ListItemIcon><WwccIcon /></ListItemIcon>
                  <ListItemText primary="WWCC" secondary="Working With Children" secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                </ListItemButton>
              )}

              {/* --- SETTINGS --- */}
              {(user?.role === 'admin' || user?.role === 'superuser') && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <ListItemButton onClick={() => setOpenSettings(!openSettings)}>
                    <ListItemIcon><SettingsIcon /></ListItemIcon>
                    <ListItemText primary="Settings" />
                    {openSettings ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>

                  <Collapse in={openSettings} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItemButton
                        sx={{ pl: 6 }} component={Link} to="/settings/staff" selected={location.pathname === '/settings/staff'}
                      >
                        <ListItemIcon><PeopleIcon /></ListItemIcon>
                        <ListItemText primary="Staff Members" />
                      </ListItemButton>


                      {/* Awards specific settings */}
                      {isModuleEnabled('awards') && (
                        <>
                          <ListItemButton
                            sx={{ pl: 6 }} component={Link} to="/settings/students" selected={location.pathname === '/settings/students'}
                          >
                            <ListItemIcon><StudentIcon /></ListItemIcon>
                            <ListItemText primary="Student Data" />
                          </ListItemButton>

                          <ListItemButton
                            sx={{ pl: 6 }} component={Link} to="/settings/awards" selected={location.pathname === '/settings/awards'}
                          >
                            <ListItemIcon><AwardsIcon /></ListItemIcon>
                            <ListItemText primary="Awards Data" />
                          </ListItemButton>
                        </>
                      )}

                      <ListItemButton
                        sx={{ pl: 6 }} component={Link} to="/settings/settings" selected={location.pathname === '/settings/settings'}
                      >
                        <ListItemIcon><TuneIcon /></ListItemIcon>
                        <ListItemText primary="System Settings" />
                      </ListItemButton>

                    </List>
                  </Collapse>
                </>
              )}

            </>
          )}
        </List>

        {/* --- BOTTOM SECTION: GLOBAL ADMIN --- */}
        {user?.role === 'superuser' && (
          <Box sx={{ pb: 2 }}>

            {/* VERSION NUMBER INDICATOR */}
            <Box
              onClick={() => setShowLog(true)}
              sx={{
                px: 2,
                pt: 1,
                cursor: 'pointer', // Makes the mouse a hand
                '&:hover': {
                  '& .MuiListItemText-secondary': {
                    color: 'primary.main', // Optional: changes color on hover for better feedback
                  }
                }
              }}
            >
              <ListItemText
                secondary={`ver: ${packageJson.version}`}
                secondaryTypographyProps={{
                  fontSize: '0.9rem',
                  color: 'text.disabled',
                  textAlign: 'left'
                }}
              />
            </Box>

            <Divider />

            <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '36px' }}>
              Superuser menu
            </ListSubheader>

            <List>
              <ListItemButton
                component={Link} to="/admin/tenants" selected={location.pathname === '/admin/tenants'}
              >
                <ListItemIcon><SchoolIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Tenants" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItemButton>

              <ListItemButton
                component={Link} to="/admin/super-users" selected={location.pathname === '/admin/super-users'}
              >
                <ListItemIcon><SuperIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Super Users" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItemButton>

              <ListItemButton
                component={Link} to="/admin/gov-files" selected={location.pathname === '/admin/gov-files'}
              >
                <ListItemIcon><FileIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Gov Files" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItemButton>

            </List>
          </Box>
        )}
      </Box>


      <VersionDialog open={showLog} onClose={() => setShowLog(false)} />
    </>
  );
}

export default NavMenu;